import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Typography, IconButton, Link,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SupplierService } from '../../services/supplier.service';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { useToast } from '../../context/ToastContext';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { DataImportService } from '../../services/integration.service';
import { MasterDataStatusFilter } from '../../interfaces/masterDataStatus.interface';
import { ActiveStatusChip, DeactivateDialog, ImportExportButtons, StatusFilterField, saveBlob } from '../common/MasterDataStatus';

export const SupplierList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SUPPLIER_MANAGE);
  const [rows, setRows] = useState<Supplier[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [supplierPendingDelete, setSupplierPendingDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState<MasterDataStatusFilter>('ACTIVE');
  const [toDeactivate, setToDeactivate] = useState<Supplier | null>(null);

  useEffect(() => { load(); }, [page, size, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      setLoading(true);
      const res = await SupplierService.listByInstitution(page, size, status);
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!supplierPendingDelete) return;
    setDeleting(true);
    try {
      await SupplierService.delete(supplierPendingDelete);
      toast.showSuccess('Supplier deleted');
      setSupplierPendingDelete(null);
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to delete supplier');
    } finally {
      setDeleting(false);
    }
  };

  const activate = async (r: Supplier) => {
    try {
      await SupplierService.activate(r.id!);
      toast.showSuccess('Supplier activated');
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to activate supplier');
    }
  };

  const exportCsv = async () => {
    try {
      saveBlob(await DataImportService.exportCsv('SUPPLIERS'), 'suppliers.csv');
    } catch (e) {
      toast.showApiError(e, 'Failed to export suppliers');
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.suppliers.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5">Suppliers</Typography>
        {canManage && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <ImportExportButtons kind="SUPPLIERS" onImport={() => navigate('/data-import?kind=SUPPLIERS')} onExport={exportCsv} />
            <Button data-help="procurement.suppliers.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/procurement/suppliers/new')}>
              New Supplier
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="procurement.suppliers.filter">
        <StatusFilterField value={status} onChange={(v) => { setStatus(v); setPage(0); }} />
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="procurement.suppliers.table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-help="procurement.suppliers.col.name">Name</TableCell>
                <TableCell data-help="procurement.suppliers.col.contact">Contact</TableCell>
                <TableCell data-help="procurement.suppliers.col.email">Email</TableCell>
                <TableCell data-help="procurement.suppliers.col.phone">Phone</TableCell>
                <TableCell data-help="procurement.suppliers.col.tax">Tax Reg No</TableCell>
                <TableCell data-help="procurement.suppliers.col.status">Status</TableCell>
                {canManage && <TableCell data-help="procurement.suppliers.col.actions" align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/procurement/suppliers/${r.id}`)}
                >
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.contactPerson ?? '—'}</TableCell>
                  <TableCell>{r.email ?? '—'}</TableCell>
                  <TableCell>{r.phone ?? '—'}</TableCell>
                  <TableCell>{r.taxRegistrationNo ?? '—'}</TableCell>
                  <TableCell><ActiveStatusChip active={r.active} reason={r.deactivationReason} /></TableCell>
                  {canManage && (
                  <TableCell align="right">
                    {r.active === false ? (
                      <Link data-help="procurement.suppliers.activate" component="button" underline="hover" sx={{ mr: 1 }} onClick={(e) => { e.stopPropagation(); activate(r); }}>Activate</Link>
                    ) : (
                      <Link data-help="procurement.suppliers.deactivate" component="button" underline="hover" sx={{ mr: 1 }} onClick={(e) => { e.stopPropagation(); setToDeactivate(r); }}>Deactivate</Link>
                    )}
                    <IconButton
                      data-help="procurement.suppliers.edit"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); navigate(`/procurement/suppliers/${r.id}/edit`); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      data-help="procurement.suppliers.delete"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setSupplierPendingDelete(r.id); }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          />
        </TableContainer>
      )}

      <ConfirmDialog
        open={supplierPendingDelete !== null}
        title="Delete this supplier?"
        message="This cannot be undone. A supplier that already has documents cannot be deleted; deactivate it instead."
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setSupplierPendingDelete(null)}
        onConfirm={handleDelete}
        submitting={deleting}
      />
      <DeactivateDialog
        open={toDeactivate !== null}
        noun="supplier"
        name={toDeactivate?.name ?? ''}
        onDeactivate={async (reason) => {
          try {
            return await SupplierService.deactivate(toDeactivate!.id!, reason);
          } catch (e) {
            toast.showApiError(e, 'Failed to deactivate supplier');
            throw e;
          }
        }}
        onClose={(changed) => { setToDeactivate(null); if (changed) load(); }}
      />
    </Box>
  );
};
